import { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import FaceScanner from '../components/FaceScanner';
import { api, type FacePunchResult } from '@/lib/api';

const actionCopy: Record<FacePunchResult['action'], { label: string; tone: string; icon: string }> = {
  'checked-in': { label: 'Checked in', tone: 'bg-[#E6F4EC] text-[#1B8A4E]', icon: 'ri-login-circle-line' },
  'checked-out': { label: 'Checked out', tone: 'bg-[#E8F0FE] text-[#1A73E8]', icon: 'ri-logout-circle-line' },
  'already-complete': { label: 'Already done for today', tone: 'bg-[#FFF8E1] text-[#E8A317]', icon: 'ri-checkbox-circle-line' },
};

export default function AttendanceKioskPage() {
  const [result, setResult] = useState<FacePunchResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [punchedAt, setPunchedAt] = useState('');

  const onCapture = async (descriptor: number[]) => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.facePunch(descriptor);
      setResult(data);
      setPunchedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not record attendance.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="text-center">
          <h2 className="text-xl font-bold text-[#3C3C3C]">Attendance Kiosk</h2>
          <p className="text-sm text-gray-500 mt-1">Staff look at the camera and tap to punch. The first scan of the day checks in; the next checks out.</p>
        </div>

        <div className="bg-white border border-[#E1E5EA] rounded-2xl p-6">
          <FaceScanner onCapture={onCapture} busy={busy} ctaLabel="Scan &amp; punch" />
        </div>

        {result && (
          <div className={`rounded-2xl p-6 flex items-center gap-4 ${actionCopy[result.action].tone}`}>
            <div className="w-14 h-14 rounded-full bg-white/60 flex items-center justify-center text-2xl">
              <i className={actionCopy[result.action].icon} />
            </div>
            <div className="flex-1">
              <p className="text-lg font-bold">{result.name}</p>
              <p className="text-sm opacity-80">{result.employeeCode} · {actionCopy[result.action].label}{result.action !== 'already-complete' ? ` at ${punchedAt}` : ''} · match {result.confidence}%</p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-2xl p-5 bg-[#FCE8E6] text-[#D93025] flex items-center gap-3">
            <i className="ri-error-warning-line text-xl" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <p className="text-center text-xs text-gray-400">
          Faces are matched against enrolled staff on the server; the camera image never leaves this device.
          Enrol staff faces from Staff HR → Employee → Face enrolment.
        </p>
      </div>
    </DashboardLayout>
  );
}
